import { dictionaryApi } from './dictionaryClient';
import {
  normalizeBartikPadachhedBarnaSamuhaLink,
  type BartikPadachhedBarnaSamuhaLink,
} from './ashtadhyayiBartikPadachhedBarnaSamuhaApi';
import {
  normalizeBartikPadachhedGanaLink,
  type BartikPadachhedGanaLink,
} from './ashtadhyayiBartikPadachhedGanaApi';
import {
  normalizeBartikPadachhedPratyaharLink,
  type BartikPadachhedPratyaharLink,
} from './ashtadhyayiBartikPadachhedPratyaharApi';

export type { BartikPadachhedBarnaSamuhaLink, BartikPadachhedGanaLink, BartikPadachhedPratyaharLink };

export type BartikPadachhed = {
  id: number;
  ashtadhyayiBartikId: number;
  order: number;
  text: string;
  bibhakti: number;
  bachan: number;
  digits: string | null;
  pratyaharLinks: BartikPadachhedPratyaharLink[];
  barnaSamuhaLinks: BartikPadachhedBarnaSamuhaLink[];
  ganaLinks: BartikPadachhedGanaLink[];
};

export type CreateBartikPadachhedBody = {
  ashtadhyayiBartikId: number;
  order: number;
  text: string;
  bibhakti: number;
  bachan: number;
  digits?: string | null;
};

export type UpdateBartikPadachhedBody = Partial<CreateBartikPadachhedBody>;

function isRecord(x: unknown): x is Record<string, unknown> {
  return x != null && typeof x === 'object' && !Array.isArray(x);
}

function unwrapEntityPayload(raw: unknown): unknown {
  if (isRecord(raw) && 'data' in raw) return raw.data;
  return raw;
}

function num(o: Record<string, unknown>, key: string): number | null {
  const v = o[key];
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  if (typeof v === 'string' && v.trim() !== '' && Number.isFinite(Number(v))) return Number(v);
  return null;
}

function str(o: Record<string, unknown>, key: string): string {
  if (!(key in o) || o[key] == null) return '';
  return String(o[key]);
}

export function normalizeBartikPadachhed(raw: unknown): BartikPadachhed | null {
  if (!isRecord(raw)) return null;
  const id = num(raw, 'id');
  const ashtadhyayiBartikId = num(raw, 'ashtadhyayiBartikId') ?? num(raw, 'ashtadhyayi_bartik_id');
  const order = num(raw, 'order');
  const bibhakti = num(raw, 'bibhakti');
  const bachan = num(raw, 'bachan');
  if (id == null || ashtadhyayiBartikId == null || order == null || bibhakti == null || bachan == null) {
    return null;
  }
  const digitsRaw = raw.digits;
  const pratyaharLinksRaw = raw.pratyaharLinks ?? raw.pratyahar_links;
  const pratyaharLinks = Array.isArray(pratyaharLinksRaw)
    ? pratyaharLinksRaw
        .map(normalizeBartikPadachhedPratyaharLink)
        .filter((x): x is BartikPadachhedPratyaharLink => x != null)
    : [];
  const barnaSamuhaLinksRaw = raw.barnaSamuhaLinks ?? raw.barna_samuha_links;
  const barnaSamuhaLinks = Array.isArray(barnaSamuhaLinksRaw)
    ? barnaSamuhaLinksRaw
        .map(normalizeBartikPadachhedBarnaSamuhaLink)
        .filter((x): x is BartikPadachhedBarnaSamuhaLink => x != null)
    : [];
  const ganaLinksRaw = raw.ganaLinks ?? raw.gana_links;
  const ganaLinks = Array.isArray(ganaLinksRaw)
    ? ganaLinksRaw.map(normalizeBartikPadachhedGanaLink).filter((x): x is BartikPadachhedGanaLink => x != null)
    : [];
  return {
    id,
    ashtadhyayiBartikId,
    order,
    text: str(raw, 'text'),
    bibhakti,
    bachan,
    digits: digitsRaw == null ? null : String(digitsRaw),
    pratyaharLinks,
    barnaSamuhaLinks,
    ganaLinks,
  };
}

function normalizeList(data: unknown): BartikPadachhed[] {
  const rows =
    Array.isArray(data) ? data : isRecord(data) && Array.isArray(data.data) ? (data.data as unknown[]) : [];
  return rows
    .map(normalizeBartikPadachhed)
    .filter((x): x is BartikPadachhed => x != null)
    .sort((a, b) => a.order - b.order);
}

export async function listBartikPadachhed(ashtadhyayiBartikId?: number): Promise<BartikPadachhed[]> {
  const params =
    ashtadhyayiBartikId != null && Number.isFinite(ashtadhyayiBartikId)
      ? { ashtadhyayiBartikId: ashtadhyayiBartikId }
      : undefined;
  const res = await dictionaryApi.get<unknown>('/ashtadhyayi-bartik-padachhed', { params });
  return normalizeList(res.data);
}

export async function getBartikPadachhed(id: number): Promise<BartikPadachhed> {
  const res = await dictionaryApi.get<unknown>(`/ashtadhyayi-bartik-padachhed/${id}`);
  const row = normalizeBartikPadachhed(unwrapEntityPayload(res.data));
  if (!row) throw new Error('Invalid bartik padachhed response');
  return row;
}

export async function createBartikPadachhed(body: CreateBartikPadachhedBody): Promise<BartikPadachhed> {
  const res = await dictionaryApi.post<unknown>('/ashtadhyayi-bartik-padachhed', body);
  const row = normalizeBartikPadachhed(unwrapEntityPayload(res.data));
  if (!row) throw new Error('Invalid bartik padachhed response');
  return row;
}

export async function updateBartikPadachhed(
  id: number,
  body: UpdateBartikPadachhedBody
): Promise<BartikPadachhed> {
  const res = await dictionaryApi.patch<unknown>(`/ashtadhyayi-bartik-padachhed/${id}`, body);
  const row = normalizeBartikPadachhed(unwrapEntityPayload(res.data));
  if (!row) throw new Error('Invalid bartik padachhed response');
  return row;
}

export async function deleteBartikPadachhed(id: number): Promise<void> {
  await dictionaryApi.delete(`/ashtadhyayi-bartik-padachhed/${id}`);
}
